<template>
  <v-container fluid class="px-0">
    <v-layout wrap>
      <v-flex>
        <v-row no-gutters>
          <v-col id="perso-top-filters" class="top-filters">
            <div
              v-for="(value, name) in topFilters"
              :id="`perso-filter-${name}`"
              :key="name"
              class="filter"
              :class="value === 0 ? 'inactive' : 'active'"
              @click="toggleTopFilter(name)"
            >
              <img :src="require(`@/assets/icons/perso/${name}.svg`)" :title="$t(`device.personalisation.state.${name}`)" :alt="$t(`device.personalisation.state.${name}`)">
              <span>{{ $t(`device.personalisation.state.${name}`) }}</span>
            </div>
          </v-col>
        </v-row>
        <v-row no-gutters align="center" class="mt-8">
          <v-col class="col-auto">
            <v-btn
              id="perso-filters-menu"
              dark
              text
              class="mr-2 px-2"
              @click="toggleFilter"
            >
              <v-icon dark>mdi-tune </v-icon>
              <span class="mx-1 main-filters">
                {{ $tc('device.filter.filters', activeFilters) }}
              </span>
              <v-icon v-if="!displayFilters" dark>mdi-menu-down</v-icon>
              <v-icon v-else dark>mdi-menu-up</v-icon>
            </v-btn>
          </v-col>
          <v-col class="col-3">
            <v-btn
              v-if="activeFilters > 0"
              id="perso-filters-clear"
              dark
              text
              class="px-2"
              @click="clearFilters"
            >
              <v-icon dark>mdi-close </v-icon>
              <span class="mx-1 main-filters">
                {{ $t('device.filter.clear_fitlers') }}
              </span>
            </v-btn>
          </v-col>
          <v-col>
            <ListToolbar
              slot="personalisation-list-toolbar"
              i18nLabel="device.history.operation"
              :state="persoListState"
              :statisticDataState="persoListState"
              :statistic="{ value: getTotalPersonalisations }"
              :paginationStart="getNavigationConfiguration.start"
              :elementsPerPage="getNavigationConfiguration.size"
              :numberOfElements="getTotalPersonalisations"
              :isLastPage="getNavigationConfiguration.isLastPersoPage"
              @page-navigation-handler="pageNavigationHandler"
              @go-to-page="navigateToPage"
            />
          </v-col>
        </v-row>
        <PersoFilters
          ref="filterComponent"
          :displayFilters="displayFilters"
          @handleActiveFiltersCount="handleActiveFiltersCount"
        />
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { MasterAccountInfo } from 'ctp-user-auth-service';
import { Status } from 'ctp-personalisation-search';
import { IPersonalisationState, INavigation, store } from '@/store/modules/personalisation/types';
import { store as userStore } from '@/store/modules/user/types';
import ListToolbar from '@/components/common/ListToolbar/ListToolbar.vue';
import PersoFilters from './PersoFilters/PersoFilters.vue';

import './styles/toolbar.scss';

@Component({
  components: {
    PersoFilters,
    ListToolbar
  }
})
export default class PersonalisationToolbar extends Vue {
  @userStore.Getter
  private getSelectedMasterAccount: MasterAccountInfo;

  @store.Getter
  private getPersonalisationState!: IPersonalisationState;

  @store.Getter
  private getTotalPersonalisations!: number;

  @store.Getter
  private getNavigationConfiguration!: INavigation;

  @store.Action
  private fetchFilterPersonalisations: (configuration: INavigation | void) => Promise<void>;

  @store.Action
  private updateNavigation!: (payload: INavigation) => void;

  @store.Action
  private persoNavigation!: (params: { direction: 'forward' | 'backward' }) => void;

  $refs!: {
    filterComponent: PersoFilters;
  };

  private activeFilters = 0;

  private selectedTopFilters:{ status: Status[], isDeperso: boolean[] } = { status: [], isDeperso: [] };

  private displayFilters = false;

  private topFilters: {[key: string]: number | string} = { incomplete: 0, perso_progress: 0, active: 0, deperso_progress: 0, deperso: 0 };

  private persoStatus = {
    incomplete: { status: [Status.Draft], isDeperso: false },
    perso_progress: { status: [Status.Pending], isDeperso: false },
    active: { status: [Status.Current], isDeperso: false },
    deperso_progress: { status: [Status.Pending], isDeperso: true },
    deperso: { status: [Status.Current], isDeperso: true }
  };

  private toggleTopFilter(filter: keyof typeof this.persoStatus) {
    const appliedFilters: { status?: Status[], isDeperso?: boolean} = {};
    // 1 filter ON, 0 filter off
    const current = this.topFilters[filter] as number;
    // Toggle on/off
    this.topFilters[filter] = 1 - current;
    // if filter ON, add status to the list of active filters.status
    // add the boolean to the list of active filters.isDeperso
    // unique values will be extracted later
    if (this.topFilters[filter] === 1) {
      this.selectedTopFilters.status.push(...this.persoStatus[filter].status);
      this.selectedTopFilters.isDeperso.push(this.persoStatus[filter].isDeperso);
    } else {
      // Remove (one of) the statuses and boolean from the list
      this.selectedTopFilters.status.splice(this.selectedTopFilters.status.findIndex(s => s === this.persoStatus[filter].status[0]), 1);
      this.selectedTopFilters.isDeperso.splice(this.selectedTopFilters.isDeperso.findIndex(s => s === this.persoStatus[filter].isDeperso), 1);
    }
    // get unique statuses and boolean, if true AND false, return undefined (to not filter on isDeperso)
    appliedFilters.status = [...new Set(this.selectedTopFilters.status)];
    appliedFilters.isDeperso = [...new Set(this.selectedTopFilters.isDeperso)].length === 1 ? [...new Set(this.selectedTopFilters.isDeperso)][0] : undefined;
    const navigation = this.getNavigationConfiguration;
    const filters = {
      ...navigation.filters,
      ...appliedFilters
    };
    navigation.start = 0;
    navigation.filters = filters;
    this.updateNavigation(navigation);
    this.fetchFilterPersonalisations(navigation);
  }

  private toggleFilter() {
    this.displayFilters = !this.displayFilters;
  }

  private clearFilters() {
    this.activeFilters = 0;
    this.$refs.filterComponent.clearFilters();
    const navigation = this.getNavigationConfiguration;
    navigation.start = 0;
    navigation.filters = {};
    this.updateNavigation(navigation);
    this.$route.params.deviceIs = '';
    this.fetchFilterPersonalisations(this.getNavigationConfiguration);
  }

  private handleActiveFiltersCount(activeFiltersCount: number) {
    this.activeFilters = activeFiltersCount;
  }

  private get persoListState(): { [key: string]: boolean } {
    return {
      fetched: this.getPersonalisationState.fetched.personalisationList,
      loading: this.getPersonalisationState.loading.personalisationList,
      error: this.getPersonalisationState.error.personalisationList
    };
  }

  private pageNavigationHandler(direction: 'forward' | 'backward') {
    this.persoNavigation({
      direction
    });
  }

  private navigateToPage(page: number) {
    const current = this.getNavigationConfiguration;
    const next = {
      ...current,
      start: page * current.size - current.size
    };
    this.updateNavigation(next);
  }
}
</script>
